/**
 * API service layer for Exelizer frontend.
 * 
 * Provides typed functions for all API endpoints.
 */

import { createApiClient } from '../config/api';
import type {
  PortfolioResponse,
  PortfolioMetadata,
  Recommendation,
  RiskRegime,
  RiskRegimeHistory,
  LadderConfig,
  RiskConfig,
  SystemHealth,
  AIIntelligence,
} from '../types';

const api = createApiClient();

/**
 * Portfolios API (Phase 10: Multi-Portfolio Support)
 */
export const portfoliosApi = {
  /**
   * List all portfolios.
   */
  async list(): Promise<{ portfolios: PortfolioMetadata[] }> {
    const response = await api.get<{ portfolios: PortfolioMetadata[] }>('/portfolios');
    return response.data;
  },

  /**
   * Get portfolio metadata by ID.
   */
  async get(portfolioId: string): Promise<{ portfolio: PortfolioMetadata }> {
    const response = await api.get<{ portfolio: PortfolioMetadata }>(
      `/portfolios/${portfolioId}`
    );
    return response.data;
  },
};

/**
 * Portfolio API
 */
export const portfolioApi = {
  /**
   * Get current portfolio state with positions.
   * 
   * @param portfolioId Optional portfolio ID (defaults to primary)
   */
  async getCurrent(portfolioId?: string): Promise<PortfolioResponse> {
    const params = portfolioId ? { portfolioId } : {};
    const response = await api.get<PortfolioResponse>('/portfolio/current', { params });
    return response.data;
  },

  /**
   * Get current portfolio state for a specific portfolio.
   * 
   * @param portfolioId Portfolio ID
   */
  async getCurrentById(portfolioId: string): Promise<PortfolioResponse> {
    const response = await api.get<PortfolioResponse>(
      `/portfolios/${portfolioId}/current`
    );
    return response.data;
  },

  /**
   * Get historical portfolio snapshot for a specific date.
   * 
   * @param date Date in YYYY-MM-DD format
   * @param portfolioId Optional portfolio ID (defaults to primary)
   */
  async getHistory(date: string, portfolioId?: string): Promise<{ snapshot: unknown; date: string }> {
    const params: Record<string, string> = { date };
    if (portfolioId) params.portfolioId = portfolioId;
    
    const response = await api.get<{ snapshot: unknown; date: string }>(
      '/portfolio/history',
      { params }
    );
    return response.data;
  },

  /**
   * Get historical portfolio snapshot for a specific portfolio and date.
   * 
   * @param portfolioId Portfolio ID
   * @param date Date in YYYY-MM-DD format
   */
  async getHistoryById(portfolioId: string, date: string): Promise<{ snapshot: unknown; date: string }> {
    const response = await api.get<{ snapshot: unknown; date: string }>(
      `/portfolios/${portfolioId}/history`,
      { params: { date } }
    );
    return response.data;
  },
};

/**
 * Recommendations API
 */
export const recommendationsApi = {
  /**
   * Get all open (PENDING) recommendations.
   * 
   * @param portfolioId Optional portfolio ID (defaults to primary)
   */
  async getOpen(portfolioId?: string): Promise<{ recommendations: Recommendation[]; count: number }> {
    const params = portfolioId ? { portfolioId } : {};
    const response = await api.get<{ recommendations: Recommendation[]; count: number }>(
      '/recommendations/open',
      { params }
    );
    return response.data;
  },

  /**
   * Get a specific recommendation by ID.
   */
  async getById(id: string): Promise<{ recommendation: Recommendation }> {
    const response = await api.get<{ recommendation: Recommendation }>(
      `/recommendations/${id}`
    );
    return response.data;
  },

  /**
   * Update recommendation status.
   */
  async updateStatus(
    id: string,
    status: Recommendation['status'],
    userNotes?: string
  ): Promise<{ recommendation: Recommendation }> {
    const response = await api.post<{ recommendation: Recommendation }>(
      `/recommendations/${id}/status`,
      { status, userNotes }
    );
    return response.data;
  },
};

/**
 * Risk Regime API
 */
export const riskApi = {
  /**
   * Get current risk regime.
   */
  async getCurrent(): Promise<RiskRegime> {
    const response = await api.get<RiskRegime>('/risk/regime/current');
    return response.data;
  },

  /**
   * Get risk regime history.
   * 
   * @param startDate Optional start date (YYYY-MM-DD)
   * @param endDate Optional end date (YYYY-MM-DD)
   */
  async getHistory(
    startDate?: string,
    endDate?: string
  ): Promise<{ history: RiskRegimeHistory[]; count: number }> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get<{ history: RiskRegimeHistory[]; count: number }>(
      '/risk/regime/history',
      { params }
    );
    return response.data;
  },
};

/**
 * Configuration API
 */
export const configApi = {
  /**
   * Get all ladder configurations.
   */
  async getLadderConfigs(): Promise<{ ladder_configs: Record<string, LadderConfig> }> {
    const response = await api.get<{ ladder_configs: Record<string, LadderConfig> }>(
      '/config/ladder'
    );
    return response.data;
  },

  /**
   * Update ladder configuration for a bucket (admin only).
   */
  async updateLadderConfig(config: LadderConfig): Promise<{ ladder_config: LadderConfig }> {
    const response = await api.post<{ ladder_config: LadderConfig }>(
      '/config/ladder',
      config
    );
    return response.data;
  },

  /**
   * Get risk regime thresholds configuration.
   */
  async getRiskConfig(): Promise<{ risk_config: RiskConfig }> {
    const response = await api.get<{ risk_config: RiskConfig }>('/config/risk');
    return response.data;
  },

  /**
   * Update risk regime thresholds (admin only, not yet implemented).
   */
  async updateRiskConfig(config: RiskConfig): Promise<{ error: string }> {
    const response = await api.post<{ error: string }>('/config/risk', config);
    return response.data;
  },

  /**
   * Get system configuration (providers, AI settings).
   */
  async getSystemConfig(): Promise<{
    system_config: {
      providers: {
        portfolioProvider: string;
        marketDataProvider: string;
        macroDataProvider: string;
      };
      ai: {
        enabled: boolean;
        advisoryEnabled: boolean;
      };
    };
  }> {
    const response = await api.get<{
      system_config: {
        providers: {
          portfolioProvider: string;
          marketDataProvider: string;
          macroDataProvider: string;
        };
        ai: {
          enabled: boolean;
          advisoryEnabled: boolean;
        };
      };
    }>('/config/system');
    return response.data;
  },

  /**
   * Update system configuration (admin only).
   */
  async updateSystemConfig(config: {
    providers?: {
      portfolioProvider?: string;
      marketDataProvider?: string;
      macroDataProvider?: string;
    };
    ai?: {
      enabled?: boolean;
      advisoryEnabled?: boolean;
    };
  }): Promise<{
    system_config: {
      providers: {
        portfolioProvider: string;
        marketDataProvider: string;
        macroDataProvider: string;
      };
      ai: {
        enabled: boolean;
        advisoryEnabled: boolean;
      };
    };
  }> {
    const response = await api.post<{
      system_config: {
        providers: {
          portfolioProvider: string;
          marketDataProvider: string;
          macroDataProvider: string;
        };
        ai: {
          enabled: boolean;
          advisoryEnabled: boolean;
        };
      };
    }>('/config/system', config);
    return response.data;
  },
};

/**
 * System Health API
 */
export const systemHealthApi = {
  /**
   * Get system health status.
   */
  async getHealth(): Promise<SystemHealth> {
    const response = await api.get<SystemHealth>('/system/health');
    return response.data;
  },
};

/**
 * Ticker API
 */
export const tickerApi = {
  /**
   * Get ticker price history for sparkline charts.
   * 
   * @param ticker Ticker symbol (e.g., "AAPL")
   * @param period Time period (1D, 7D, 1M, 1Y). Default: 1M
   */
  async getHistory(ticker: string, period: '1H' | '12H' | '1D' | '7D' | '1M' | '1Y' = '1M'): Promise<{
    ticker: string;
    period: string;
    data: Array<{
      date: string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume: number;
    }>;
  }> {
    const response = await api.get<{
      ticker: string;
      period: string;
      data: Array<{
        date: string;
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number;
      }>;
    }>(`/tickers/${ticker}/history`, { params: { period } });
    return response.data;
  },
};

/**
 * AI Intelligence API (Phase 7.8)
 */
export const aiIntelligenceApi = {
  /**
   * Get AI intelligence from latest snapshot.
   * Falls back to latest available snapshot if date not specified.
   */
  async getIntelligence(date?: string): Promise<AIIntelligence> {
    try {
      // Get latest snapshot
      const today = date || new Date().toISOString().split('T')[0];
      const snapshotResponse = await portfolioApi.getHistory(today);
      
      // Extract AI advisory from snapshot
      const snapshot = snapshotResponse.snapshot as any;
      const aiAdvisory = snapshot?.ai_advisory;
      
      if (!aiAdvisory) {
        return {
          status: 'unavailable',
        };
      }
      
      return {
        crossMarketIntelligence: aiAdvisory.crossMarketIntelligence,
        emergingOpportunities: aiAdvisory.emergingOpportunities,
        status: 'success',
        model: aiAdvisory.metadata?.model,
        lastUpdatedAt: aiAdvisory.metadata?.timestamp
          ? new Date(aiAdvisory.metadata.timestamp * 1000).toISOString()
          : undefined,
      };
    } catch (error) {
      console.error('Failed to load AI intelligence:', error);
      return {
        status: 'error',
      };
    }
  },
};

/**
 * Jobs API (Admin only)
 */
export const jobsApi = {
  /**
   * Trigger portfolio batch for all portfolios.
   */
  async triggerPortfolioBatchAll(): Promise<{ status: string; message: string; result?: any }> {
    const response = await api.post<{ status: string; message: string; result?: any }>(
      '/admin/jobs/portfolio-batch'
    );
    return response.data;
  },

  /**
   * Trigger portfolio batch for a single portfolio.
   */
  async triggerPortfolioBatchSingle(portfolioId: string): Promise<{ status: string; message: string; messageId?: string }> {
    const response = await api.post<{ status: string; message: string; messageId?: string }>(
      `/admin/jobs/portfolio-batch/${portfolioId}`
    );
    return response.data;
  },

  /**
   * Trigger global daily email.
   */
  async triggerGlobalEmail(): Promise<{ status: string; message: string; result?: any }> {
    const response = await api.post<{ status: string; message: string; result?: any }>(
      '/admin/jobs/global-email'
    );
    return response.data;
  },

  /**
   * Trigger portfolio emails for all portfolios.
   */
  async triggerPortfolioEmail(): Promise<{ status: string; message: string; results?: any; summary?: any }> {
    const response = await api.post<{ status: string; message: string; results?: any; summary?: any }>(
      '/admin/jobs/portfolio-email'
    );
    return response.data;
  },
};

